import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCurrency } from "@/lib/currencies";
import { CurrencySelect } from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  Download,
  CalendarIcon,
  Paperclip,
  Loader2,
  Sparkles,
  Upload,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  Clock
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialExpense = {
  title: "",
  amount: "",
  currency: "EUR",
  category_id: "",
  invoice_number: "",
  description: "",
  expense_type: "variable",
  vendor: "",
  date: new Date().toISOString().split("T")[0],
  attachment_name: null,
  attachment_data: null,
  status: "draft",
  payment_status: "open",
};

const STATUS_COLORS = {
  draft: "bg-gray-500",
  pending_review: "bg-amber-500",
  reviewed: "bg-blue-500",
  booked: "bg-green-500",
};

const STATUS_KEYS = {
  draft: "review.draft",
  pending_review: "review.pendingReview",
  reviewed: "review.reviewed",
  booked: "review.booked",
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState(initialExpense);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [analyzing, setAnalyzing] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const { t } = useLanguage();

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/expenses`),
        axios.get(`${API}/categories`),
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for duplicate invoice number
  const checkDuplicateInvoice = useCallback(async (invoiceNumber) => {
    if (!invoiceNumber || !invoiceNumber.trim()) {
      setDuplicateWarning(null);
      return false;
    }

    setCheckingDuplicate(true);
    try {
      const response = await axios.post(`${API}/expenses/check-duplicate`, {
        invoice_number: invoiceNumber.trim(),
        exclude_expense_id: editingExpense?.id || null
      });

      if (response.data.is_duplicate) {
        const existing = response.data.existing_expense;
        setDuplicateWarning({
          message: response.data.message,
          existing: existing
        });
        return true;
      } else {
        setDuplicateWarning(null);
        return false;
      }
    } catch (error) {
      console.error("Duplicate check error:", error);
      return false;
    } finally {
      setCheckingDuplicate(false);
    }
  }, [editingExpense]);

  const handleOpenDialog = (expense = null) => {
    setDuplicateWarning(null);
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        title: expense.title,
        amount: expense.amount.toString(),
        currency: expense.currency || "EUR",
        category_id: expense.category_id,
        invoice_number: expense.invoice_number || "",
        description: expense.description || "",
        expense_type: expense.expense_type,
        vendor: expense.vendor || "",
        date: expense.date,
        attachment_name: expense.attachment_name,
        attachment_data: expense.attachment_data,
        payment_status: expense.payment_status || "open",
      });
      setSelectedDate(parseISO(expense.date));
    } else {
      setEditingExpense(null);
      setFormData(initialExpense);
      setSelectedDate(new Date());
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setFormData(initialExpense);
    setDuplicateWarning(null);
  };

  const handleFileChangeWithAI = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datei darf maximal 5MB groß sein");
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result;
      
      // Set attachment data
      setFormData(prev => ({
        ...prev,
        attachment_name: file.name,
        attachment_data: base64Data,
      }));
      
      // Only analyze images
      const isImage = file.type.startsWith('image/') || file.type === 'application/pdf';
      if (!isImage) {
        toast.info("KI-Analyse nur für Bilder und PDFs verfügbar");
        return;
      }
      
      // Analyze with AI
      setAnalyzing(true);
      toast.info("KI analysiert Dokument...", { duration: 10000, id: "analyzing" });
      
      try {
        const response = await axios.post(`${API}/analyze-document`, {
          image_base64: base64Data,
          filename: file.name
        });
        
        toast.dismiss("analyzing");
        
        const data = response.data;
        
        // Update form with AI-extracted data
        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          amount: data.amount?.toString() || prev.amount,
          currency: data.currency || prev.currency,
          vendor: data.vendor || prev.vendor,
          invoice_number: data.invoice_number || prev.invoice_number,
          description: data.description || prev.description,
        }));
        
        // Update date if extracted
        if (data.date) {
          try {
            setSelectedDate(parseISO(data.date));
          } catch (e) {
            console.error("Invalid date:", data.date);
          }
        }
        
        // Try to match category
        if (data.category_suggestion && categories.length > 0) {
          const matchedCategory = categories.find(
            cat => cat.name.toLowerCase().includes(data.category_suggestion.toLowerCase()) ||
                   data.category_suggestion.toLowerCase().includes(cat.name.toLowerCase())
          );
          if (matchedCategory) {
            setFormData(prev => ({ ...prev, category_id: matchedCategory.id }));
          }
        }
        
        // Check for duplicate invoice number after AI extraction
        if (data.invoice_number) {
          await checkDuplicateInvoice(data.invoice_number);
        }
        
        const confidence = data.confidence || "mittel";
        if (confidence === "hoch") {
          toast.success("Dokument erfolgreich analysiert! Bitte Daten prüfen.");
        } else if (confidence === "mittel") {
          toast.success("Dokument analysiert. Einige Felder könnten unvollständig sein.");
        } else {
          toast.warning("Dokument teilweise erkannt. Bitte manuell ergänzen.");
        }
        
      } catch (error) {
        toast.dismiss("analyzing");
        console.error("AI Analysis error:", error);
        toast.error("KI-Analyse fehlgeschlagen. Bitte manuell ausfüllen.");
      } finally {
        setAnalyzing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Datei darf maximal 5MB groß sein");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          attachment_name: file.name,
          attachment_data: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate before saving
    if (formData.invoice_number && formData.invoice_number.trim()) {
      const isDuplicate = await checkDuplicateInvoice(formData.invoice_number);
      if (isDuplicate) {
        toast.error("Diese Rechnungsnummer existiert bereits! Bitte prüfen Sie die Eingabe.");
        return;
      }
    }
    
    setSaving(true);

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: format(selectedDate, "yyyy-MM-dd"),
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success("Ausgabe aktualisiert");
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success("Ausgabe erstellt");
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diese Ausgabe wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success("Ausgabe gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/expenses`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ausgaben_export_${format(new Date(), "yyyyMMdd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export erfolgreich");
    } catch (error) {
      toast.error("Export fehlgeschlagen");
    }
  };

  const filteredExpenses = expenses.filter((expense) => {
    const matchesSearch = expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || expense.expense_type === filterType;
    const matchesCategory = filterCategory === "all" || expense.category_id === filterCategory;
    const matchesPaymentStatus = filterPaymentStatus === "all" || (expense.payment_status || "open") === filterPaymentStatus;
    return matchesSearch && matchesType && matchesCategory && matchesPaymentStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("expenses.title")}</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            {t("expenses.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={handleExport}
            data-testid="export-btn"
            size="sm"
            className="md:size-default"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t("expenses.export")}</span>
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#E31E24] hover:bg-[#c91a1f]"
                onClick={() => handleOpenDialog()}
                data-testid="add-expense-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("expenses.newExpense")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {editingExpense ? t("expenses.editExpense") : t("expenses.newExpense")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4 mt-2 md:mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="title">{t("expenses.expenseTitle")} *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="z.B. Microsoft 365 Lizenz"
                      required
                      data-testid="expense-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">{t("expenses.amount")} *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        required
                        className="flex-1"
                        data-testid="expense-amount"
                      />
                      <CurrencySelect
                        value={formData.currency}
                        onValueChange={(value) => setFormData({ ...formData, currency: value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t("expenses.date")} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="expense-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(selectedDate, "dd.MM.yyyy", { locale: de })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => date && setSelectedDate(date)}
                          locale={de}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="category">{t("expenses.category")} *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="expense-category">
                        <SelectValue placeholder={t("common.search")} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="type">{t("expenses.type")} *</Label>
                    <Select
                      value={formData.expense_type}
                      onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
                    >
                      <SelectTrigger data-testid="expense-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="variable">{t("expenses.variable")}</SelectItem>
                        <SelectItem value="fix">{t("expenses.fix")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="vendor">{t("expenses.vendor")}</Label>
                    <Input
                      id="vendor"
                      value={formData.vendor}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      placeholder="z.B. Microsoft GmbH"
                      data-testid="expense-vendor"
                    />
                  </div>
                  <div>
                    <Label>{t("expenses.paymentStatus")}</Label>
                    <Select
                      value={formData.payment_status}
                      onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                    >
                      <SelectTrigger data-testid="expense-payment-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">
                          <span className="flex items-center gap-2">
                            <Clock className="h-3 w-3 text-amber-500" />
                            {t("expenses.open")}
                          </span>
                        </SelectItem>
                        <SelectItem value="paid">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            {t("expenses.paid")}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="invoice_number">{t("expenses.invoiceNumber")}</Label>
                    <div className="relative">
                      <Input
                        id="invoice_number"
                        value={formData.invoice_number}
                        onChange={(e) => {
                          setFormData({ ...formData, invoice_number: e.target.value });
                          // Clear warning when user types
                          if (duplicateWarning) setDuplicateWarning(null);
                        }}
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            checkDuplicateInvoice(e.target.value);
                          }
                        }}
                        placeholder="z.B. RE-2024-001"
                        data-testid="expense-invoice-number"
                        className={duplicateWarning ? "border-red-500 pr-10" : ""}
                      />
                      {checkingDuplicate && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                      {duplicateWarning && !checkingDuplicate && (
                        <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                      )}
                    </div>
                    {duplicateWarning && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 text-sm">
                            <p className="font-medium text-red-700">{duplicateWarning.message}</p>
                            {duplicateWarning.existing && (
                              <div className="mt-2 text-red-600 space-y-1">
                                <p className="font-semibold">{t("expenses.expenseTitle")}:</p>
                                <p>• {t("expenses.expenseTitle")}: {duplicateWarning.existing.title}</p>
                                <p>• {t("expenses.amount")}: {duplicateWarning.existing.amount} {duplicateWarning.existing.currency}</p>
                                <p>• {t("expenses.date")}: {duplicateWarning.existing.date}</p>
                                <p>• {t("common.status")}: <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  duplicateWarning.existing.status === 'draft' ? 'bg-amber-100 text-amber-800' :
                                  duplicateWarning.existing.status === 'pending_review' ? 'bg-amber-100 text-amber-800' :
                                  duplicateWarning.existing.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                                  duplicateWarning.existing.status === 'booked' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {t(STATUS_KEYS[duplicateWarning.existing.status]) || duplicateWarning.existing.status}
                                </span></p>
                                {(duplicateWarning.existing.status === 'draft' || duplicateWarning.existing.status === 'pending_review') && (
                                  <p className="mt-2 text-amber-700 font-medium">
                                    ⚠️ {t("expenses.duplicateInReview")}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="description">{t("expenses.description")}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Zusätzliche Informationen..."
                      rows={2}
                      data-testid="expense-description"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="attachment">{t("expenses.attachment")} + {t("expenses.aiAnalysis")}</Label>
                    <div className="mt-2 p-4 border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-[#E31E24]/50 transition-colors">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          {analyzing ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin text-[#E31E24]" />
                              <span className="text-sm">{t("expenses.aiAnalysis")}...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-5 w-5 text-[#E31E24]" />
                              <span className="text-sm font-medium">{t("expenses.attachment")}</span>
                            </>
                          )}
                        </div>
                        <Input
                          id="attachment"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChangeWithAI}
                          className="max-w-xs"
                          disabled={analyzing}
                          data-testid="expense-attachment"
                        />
                        {formData.attachment_name && (
                          <Badge variant="secondary" className="mt-1">
                            <Paperclip className="h-3 w-3 mr-1" />
                            {formData.attachment_name}
                          </Badge>
                        )}
                        <p className="text-xs text-muted-foreground text-center">
                          PDF, JPG oder PNG hochladen - KI erkennt automatisch Betrag, Datum, Anbieter
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    {t("common.cancel")}
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#E31E24] hover:bg-[#c91a1f]"
                    disabled={saving}
                    data-testid="save-expense-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("common.loading")}
                      </>
                    ) : (
                      t("common.save")
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("expenses.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder={t("expenses.type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("expenses.all")}</SelectItem>
                  <SelectItem value="fix">{t("expenses.fix")}</SelectItem>
                  <SelectItem value="variable">{t("expenses.variable")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder={t("expenses.category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("expenses.all")}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
                <SelectTrigger data-testid="filter-payment-status">
                  <SelectValue placeholder={t("expenses.paymentStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("expenses.allPaymentStatus")}</SelectItem>
                  <SelectItem value="open">{t("expenses.open")}</SelectItem>
                  <SelectItem value="paid">{t("expenses.paid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {filteredExpenses.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              {t("expenses.noExpenses")}
            </CardContent>
          </Card>
        ) : (
          filteredExpenses.map((expense) => (
            <Card key={expense.id} className="card-hover" data-testid={`expense-card-${expense.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{expense.title}</h3>
                      {expense.attachment_name && (
                        <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      )}
                      {expense.status && STATUS_COLORS[expense.status] && (
                        <Badge className={`${STATUS_COLORS[expense.status]} text-white text-xs`}>
                          {t(STATUS_KEYS[expense.status])}
                        </Badge>
                      )}
                      <Badge 
                        variant="outline"
                        className={`text-xs ${(expense.payment_status || "open") === "paid" ? "border-green-500 text-green-600" : "border-amber-500 text-amber-600"}`}
                        data-testid={`payment-status-badge-${expense.id}`}
                      >
                        {(expense.payment_status || "open") === "paid" ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" />{t("expenses.paid")}</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" />{t("expenses.open")}</>
                        )}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {format(parseISO(expense.date), "dd.MM.yyyy")} • {expense.vendor || "—"}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <div 
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: expense.category?.color || "#71717a" }}
                      />
                      <span className="text-xs text-muted-foreground">{expense.category?.name || "—"}</span>
                      <Badge 
                        variant="secondary"
                        className={`text-xs ${expense.expense_type === "fix" ? "badge-fix" : "badge-variable"}`}
                      >
                        {expense.expense_type === "fix" ? "Fix" : "Var"}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-bold font-[JetBrains_Mono]">
                      {formatCurrency(expense.amount, expense.currency)}
                    </p>
                    {expense.currency && expense.currency !== "EUR" && expense.amount_eur != null && (
                      <p className="text-xs text-muted-foreground font-[JetBrains_Mono]">
                        ≈ {formatCurrency(expense.amount_eur, "EUR")}
                      </p>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenDialog(expense)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(expense.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">{t("expenses.date")}</TableHead>
                <TableHead>{t("expenses.expenseTitle")}</TableHead>
                <TableHead>{t("expenses.category")}</TableHead>
                <TableHead>{t("expenses.vendor")}</TableHead>
                <TableHead className="text-center">{t("expenses.type")}</TableHead>
                <TableHead className="text-center">{t("common.status")}</TableHead>
                <TableHead className="text-center">{t("expenses.paymentStatus")}</TableHead>
                <TableHead className="text-right">{t("expenses.amount")}</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {t("expenses.noExpenses")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} className="table-row-hover" data-testid={`expense-row-${expense.id}`}>
                    <TableCell className="font-medium">
                      {format(parseISO(expense.date), "dd.MM.yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {expense.title}
                        {expense.attachment_name && (
                          <Paperclip className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: expense.category?.color || "#71717a" }}
                        />
                        {expense.category?.name || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {expense.vendor || "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="secondary"
                        className={expense.expense_type === "fix" ? "badge-fix" : "badge-variable"}
                      >
                        {expense.expense_type === "fix" ? t("expenses.fix") : t("expenses.variable")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {expense.status && STATUS_COLORS[expense.status] && (
                        <Badge className={`${STATUS_COLORS[expense.status]} text-white`}>
                          {t(STATUS_KEYS[expense.status])}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="outline"
                        className={`${(expense.payment_status || "open") === "paid" ? "border-green-500 text-green-600" : "border-amber-500 text-amber-600"}`}
                        data-testid={`payment-status-${expense.id}`}
                      >
                        {(expense.payment_status || "open") === "paid" ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" />{t("expenses.paid")}</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" />{t("expenses.open")}</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-[JetBrains_Mono] font-medium">
                      <div>{formatCurrency(expense.amount, expense.currency)}</div>
                      {expense.currency && expense.currency !== "EUR" && expense.amount_eur != null && (
                        <div className="text-xs text-muted-foreground">
                          ≈ {formatCurrency(expense.amount_eur, "EUR")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(expense)}
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(expense.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
